import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// Section标题容器
const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
  borderBottom: theme.palette.mode === "dark"
    ? "0.5px solid rgba(84, 84, 88, 0.65)"
    : "0.5px solid rgba(0, 0, 0, 0.1)",
}));

// 活动列表项 - 企业化设计
const ActivityItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 0),
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  borderRadius: "8px",
  px: 1.5,
  mx: -1.5,
  "&:active": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.03)",
  },
}));

const MobileVision = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const location = useLocation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/vision-space`);
        setActivities(response.data);
      } catch (err) {
        console.error("获取活动数据失败:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  // 处理URL hash，滚动到对应的section
  useEffect(() => {
    const hash = location.hash;
    if (hash && activities.length > 0) {
      const sectionId = decodeURIComponent(hash.substring(1));
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 48;
          const yOffset = -navbarHeight - 16;
          const elementTop = element.getBoundingClientRect().top;
          const elementPosition = elementTop + window.pageYOffset;
          const offsetPosition = elementPosition + yOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  }, [activities, location.hash]);

  // 获取所有唯一的类型，并确保清晨灵修事工在最前面
  const getUniqueTypes = () => {
    const types = activities.map((activity) => activity.type);
    const uniqueTypes = [...new Set(types)];
    const sortedTypes = uniqueTypes.sort((a, b) => {
      if (a === "清晨灵修事工") return -1;
      if (b === "清晨灵修事工") return 1;
      return a.localeCompare(b);
    });
    return sortedTypes;
  };

  const handleCardClick = (activity) => {
    navigate(`/vision/${activity._id}`);
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      {/* 页面标题 */}
      <Typography
        variant="h4"
        sx={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: textColor,
          mb: 3,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
        }}
      >
        异象空间
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: textColor }} />
        </Box>
      ) : activities.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: secondaryTextColor, fontSize: "0.9rem" }}>
            暂无活动数据
          </Typography>
        </Box>
      ) : (
        <>
          {getUniqueTypes().map((type, typeIndex) => {
            const typeActivities = activities.filter((activity) => activity.type === type);
            return (
              <Box key={type} id={type} sx={{ mb: 4, scrollMarginTop: "64px" }}>
                {/* Section标题 */}
                <SectionHeader>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: prefersDarkMode
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.06)",
                      color: textColor,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    {typeIndex + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: textColor,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {type}
                  </Typography>
                </SectionHeader>

                {/* 活动列表 */}
                <Box>
                  {typeActivities.map((activity, index) => {
                    const isUpcoming = activity.status === "upcoming";
                    const date = new Date(activity.date);
                    const now = new Date();
                    const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

                    return (
                      <React.Fragment key={activity._id}>
                        <ActivityItem onClick={() => handleCardClick(activity)}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  color: textColor,
                                  mb: 0.5,
                                  lineHeight: 1.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {activity.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: secondaryTextColor,
                                  mb: 0.75,
                                  lineHeight: 1.4,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {activity.description}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeIcon sx={{ fontSize: "0.7rem", color: secondaryTextColor }} />
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: secondaryTextColor,
                                }}
                              >
                                {date.toLocaleDateString()}
                              </Typography>
                            </Box>
                            {isUpcoming && (
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: secondaryTextColor,
                                }}
                              >
                                · 还有 {daysUntil} 天
                              </Typography>
                            )}
                            {activity.mediaType && (
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: secondaryTextColor,
                                }}
                              >
                                · {activity.mediaType === "video" ? "视频" : activity.mediaType === "audio" ? "音频" : "无媒体"}
                              </Typography>
                            )}
                          </Box>
                        </ActivityItem>
                        {index < typeActivities.length - 1 && (
                          <Divider
                            sx={{
                              borderColor: borderColor,
                              my: 0,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </>
      )}

    </Box>
  );
};

export default MobileVision;
